import Constants from '@/assets/Constants'
import { APIError } from '@/data'
import { APILogger, LogItem } from '@/loggers/APILogger'
import path from 'path'
import { APIRequestConfig, APIRoute } from '@/modules/utils'
import { _removeItem, TokenKeys } from '@/modules/Auth/LocalStorageHelper'
import { ObjectEntries } from '@/utils'

export type ErrorManager = {
  internal?: string
  message?: string
  fields?: Record<string, string>
}

export type APIResponseBaseType<T> = T & { text?: string; message?: string; errorManager?: ErrorManager }

function calculateAPIPath<RequestUrlPathParams extends Record<string, string>>(
  route: APIRoute,
  params?: RequestUrlPathParams,
): string {
  let result = route.path
  if (params) {
    ObjectEntries(params).forEach(([param, value]) => {
      result = result.replace(`:${String(param)}`, value)
    })
  }
  return result
}

/**
 * Helper used to manage authentication routes & information
 */
class AuthApi {
  apiUrl: URL

  headers: Record<string, string>

  constructor() {
    this.apiUrl = new URL(Constants.apiUrl)
    this.headers = {
      Accept: 'application/ld+json',
      'X-App-Platform': Constants.xAppPlatform,
      'X-App-Brand': Constants.brand,
      'X-App-Source': 'client',
      'X-App-Locale': Constants.defaultLocal,
    }
  }

  /**
   * Method to add global headers
   * @returns The Record of all Headers
   */
  setGlobalHeader = (header: string, value: string | null) => {
    if (value) {
      this.headers[header] = value
    } else {
      delete this.headers[header]
    }

    return this.headers
  }

  setLocale = (locale: string) => {
    return this.setGlobalHeader('X-App-Locale', locale)
  }

  /**
   * Used to parse a request Response from API
   * @param requestResponse
   * @param isBlob True if we need to get blob instead of json data
   * @returns All information in JS object { status, json, blob, logs }
   */
  async parseRequestResponseAsync<T>(requestResponse?: Response, isBlob?: boolean) {
    const logs: LogItem[] = []
    let status = -1
    let json: Record<string, any> = {}
    let blob: Blob | undefined

    if (requestResponse) {
      ;({ status } = requestResponse)
      if (Constants.enableRequestsLogger) {
        logs.push({ title: 'Status', content: status })
      }
      let jsonFailed = false
      if (!isBlob) {
        try {
          const type = requestResponse.headers.get('Content-Type')
          if (type && (type.includes('application/json') || type.includes('application/ld+json'))) {
            json = await requestResponse.json()
          } else {
            logs.push({ title: 'Response type', content: type })
            jsonFailed = true
          }
        } catch (error) {
          jsonFailed = true
          logs.push({ title: 'Error', content: `Could not parse JSON content with message: ${error.message}` })
        }
      }
      if (isBlob || jsonFailed) {
        try {
          blob = await requestResponse.blob()
        } catch (error) {
          logs.push({ title: 'Error', content: `Could not parse BLOB content with message: ${error.message}` })
          try {
            json = { text: await requestResponse.text() }
          } catch (errText) {
            logs.push({ title: 'Error', content: `Could not parse TEXT content with message: ${error.message}` })
          }
        }
      }

      if (Constants.enableRequestsLogger) {
        logs.push({ title: 'Data', content: json })
      }
    }

    return {
      status,
      json: json as APIResponseBaseType<T>,
      blob,
      logs,
    }
  }

  /**
   * Used to start a request, with logger (if enabled in Constants), with response parsing
   * @param url The URL of the route
   * @param params The params of the fetch request
   * @returns <T> is a generic type to allow different kind of returns
   */
  async handleAPIRequestAsync<T>(url: URL, params: RequestInit): Promise<T> {
    let response: Response | null

    const isFormData = params.body instanceof FormData

    try {
      response = await fetch(url.toString(), params)
    } catch (error) {
      APILogger.logStart(params.method, url, 0)
      APILogger.logRequest(params.method, url, !isFormData ? params.body : undefined)
      APILogger.logHeaders(params.headers)
      if (params.body instanceof FormData) {
        APILogger.logFormData(params.body)
      }
      APILogger.logResponse([{ title: 'Error', content: `Request failed with message: ${error.message}` }])
      APILogger.logFinalStep()

      throw error
    }

    const { status, json, logs } = await this.parseRequestResponseAsync<T>(response)

    APILogger.logStart(params.method, url, status)
    APILogger.logRequest(params.method, url, !isFormData ? params.body : undefined)
    APILogger.logHeaders(params.headers)
    if (params.body instanceof FormData) {
      APILogger.logFormData(params.body)
    }
    APILogger.logResponse(logs)
    APILogger.logFinalStep()

    if (status >= 200 && status < 300) {
      return json
    }

    const { errorManager } = json

    const {
      internal: message = json.text || json.message,
      message: errorManagerMessage,
      fields: errorManagerFields,
    } = errorManager || {}

    if (errorManagerMessage === 'Session expiree.') {
      _removeItem(TokenKeys.Auth)
      _removeItem(TokenKeys.Refresh)
      _removeItem(TokenKeys.Mercure)
      _removeItem(TokenKeys.MercureUrl)
    }

    throw new APIError(message || 'UNKNOWN', status, errorManagerMessage, errorManagerFields)
  }

  /**
   * @deprecated
   * Used to send a generic authenticated request (will append auth header if present)
   * @param routePath The path of the route
   * @param method The HTTP method
   * @param token The Bearer token if needed
   * @param body The body of the request
   * @param headers The additional headers to send
   * @returns  <T> is a generic type to allow different kind of returns
   */
  async requestAuthenticatedAsync<T>(
    routePath: string,
    method: string,
    token?: string | null,
    body?: object | FormData,
    headers?: Record<string, string>,
  ): Promise<T> {
    const finalHeaders: Record<string, string> = {
      ...this.headers,
      ...headers,
    }

    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`
    }

    const params: RequestInit = {
      method,
      headers: finalHeaders,
      redirect: 'follow',
    }

    if (body) {
      if (body instanceof FormData) {
        params.body = body
        delete finalHeaders['Content-Type']
      } else {
        params.body = JSON.stringify(body)
      }
    }

    const url = new URL(path.join(this.apiUrl.pathname, routePath), this.apiUrl)

    return this.handleAPIRequestAsync<T>(url, params)
  }

  async requestAsync<
    ResponseBodyType,
    RequestBodyType extends Record<string, any> | FormData = never,
    RequestUrlPathParams extends Record<string, string> = never,
    RequestUrlSearchParams extends Record<string, any> = never,
  >(
    route: APIRoute,
    config: APIRequestConfig<RequestBodyType, RequestUrlPathParams, RequestUrlSearchParams> = {},
    token?: string | null,
  ): Promise<ResponseBodyType> {
    const { body, urlPathParams, searchParams, headers, ...conf } = config || {}

    const additionalHeaders: Record<string, string> = {}
    if (token) {
      additionalHeaders.Authorization = `Bearer ${token}`
    }

    let finalBody: BodyInit | null = null
    if (body) {
      if (body instanceof FormData) {
        finalBody = body
      } else {
        finalBody = JSON.stringify(body)
        additionalHeaders['Content-Type'] = 'application/json;charset=UTF-8'
      }
    }

    const finalHeaders: HeadersInit = {
      ...this.headers,
      ...headers,
      ...additionalHeaders,
    }

    const init: RequestInit = {
      ...conf,
      method: route.method,
      headers: finalHeaders,
      redirect: 'follow',
      body: finalBody,
      credentials: 'include',
    }

    const pathWithPathParams = calculateAPIPath<RequestUrlPathParams>(route, urlPathParams)

    const url = new URL(pathWithPathParams, this.apiUrl)
    if (searchParams) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            url.searchParams.append(`${key}[]`, val)
          })
        } else {
          url.searchParams.append(key, value)
        }
      })
    }

    return await this.handleAPIRequestAsync<ResponseBodyType>(url, init)
  }
}

const AuthApiSingleton = new AuthApi()

export default AuthApiSingleton
